/** The daily action list — worst variance first (MASTER_PROMPT section 8.1). */

import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";
import type { Machine, Run, User } from "../../lib/types";
import {
  fmtDate, fmtPct, fmtQty, fmtShift, fmtStatus, toNumber,
} from "../../lib/format";

export function VarianceQueue({ user }: { user: User }) {
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    Promise.all([api.pendingRuns(), api.machines()])
      .then(([r, m]) => {
        setRuns(r);
        setMachines(m);
      })
      .catch((e: ApiError) => setError(e.message));
  }, []);

  useEffect(load, [load]);

  const nameOfMachine = useCallback(
    (id: number) => machines.find((m) => m.id === id)?.name ?? `Machine ${id}`,
    [machines]
  );

  // Declared here rather than at module scope because the machine column
  // resolves an id against the masters this screen has loaded.
  const columns = useMemo<SheetColumn<Run>[]>(
    () => [
      { key: "machine", label: "Machine", value: (r) => nameOfMachine(r.machine_id) },
      { key: "date", label: "Date", type: "date",
        value: (r) => r.business_date, format: (r) => fmtDate(r.business_date) },
      { key: "shift", label: "Shift", value: (r) => fmtShift(r.shift) },
      { key: "in", label: "In", type: "number", numeric: true,
        value: (r) => toNumber(r.input_qty), format: (r) => fmtQty(r.input_qty, 2) },
      { key: "out", label: "Out", type: "number", numeric: true,
        value: (r) => toNumber(r.output_qty), format: (r) => fmtQty(r.output_qty, 2) },
      { key: "declared", label: "Declared", type: "number", numeric: true,
        value: (r) => toNumber(r.declared_loss),
        format: (r) => fmtQty(r.declared_loss, 2) },
      { key: "unaccounted", label: "Unaccounted", type: "number", numeric: true,
        value: (r) => toNumber(r.variance_qty),
        format: (r) => fmtQty(r.variance_qty, 2) },
      { key: "variance", label: "Variance", type: "number", numeric: true,
        value: (r) => toNumber(r.variance_pct),
        format: (r) => fmtPct(r.variance_pct) },
      { key: "status", label: "Status", value: (r) => fmtStatus(r.status) },
    ],
    [nameOfMachine]
  );

  const sheet = useSheet(runs ?? [], columns);

  async function approve(run: Run) {
    try {
      await api.approveRun(run.id);
      load();
    } catch (e) {
      setError((e as ApiError).message);
    }
  }

  if (error) {
    return (
      <div className="state state--error">
        <p>{error}</p>
        <button className="btn btn--ghost" onClick={load}>
          Try again
        </button>
      </div>
    );
  }

  if (runs === null) {
    return (
      <div className="state">
        {[0, 1, 2].map((i) => (
          <div key={i} className="skeleton state__bar" />
        ))}
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="state state--empty">
        <span className="state__glyph" aria-hidden="true">
          ✓
        </span>
        <h3>Nothing waiting</h3>
        <p>
          Every submitted shift has been verified. New submissions appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Variance queue</h2>
          <p className="page__sub">
            {runs.length} shift{runs.length === 1 ? "" : "s"} awaiting
            verification, largest unaccounted quantity first
          </p>
        </div>
      </header>

      <SheetToolbar sheet={sheet} exportName="variance-queue" />

      <div className="tablewrap">
        <table className="table">
          <SheetHead sheet={sheet} trailing={<th key="verify" />} />
          <tbody>
            {sheet.rows.map((run) => {
              const off = run.within_tolerance === false;
              const mine = run.submitted_by === user.id;
              return (
                <tr key={run.id}>
                  <td>{nameOfMachine(run.machine_id)}</td>
                  <td>{fmtDate(run.business_date)}</td>
                  <td>{fmtShift(run.shift)}</td>
                  <td className="num mono">{fmtQty(run.input_qty, 2)}</td>
                  <td className="num mono">{fmtQty(run.output_qty, 2)}</td>
                  <td className="num mono">{fmtQty(run.declared_loss, 2)}</td>
                  <td className={`num mono${off ? " cell--negative" : ""}`}>
                    {/* Arrow as well as colour — meaning never rides on hue
                        alone (section 7.5). */}
                    {off ? "▲ " : ""}
                    {fmtQty(run.variance_qty, 2)}
                  </td>
                  <td className={`num mono${off ? " cell--negative" : ""}`}>
                    {fmtPct(run.variance_pct)}
                  </td>
                  <td>
                    <span className={`pill${off ? " pill--warning" : ""}`}>
                      {fmtStatus(run.status)}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn--ghost btn--sm"
                      onClick={() => approve(run)}
                      disabled={mine}
                      title={
                        mine
                          ? "You submitted this shift, so someone else must verify it"
                          : "Verify this shift"
                      }
                    >
                      Verify
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
